import React, { useState, useContext } from "react";
import { UserContext, API } from "@/App";
import { STORE_CATEGORIES, RANKS, GOLD_COIN_URL } from "@/data/gameData";
import { playCoinSound } from "@/data/sounds";
import { Lock, Check, ArrowLeft, ChevronRight, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const CAT_EMOJI = { pants: "👖", shirt: "👕", bandana: "🧣", hat: "🎩", weapon: "⚔️", tool: "🔭" };
const CAT_ORDER = ["hat", "bandana", "shirt", "pants", "weapon", "tool"];

// Silhouette with equipped accessories
function OutfitDisplay({ accessories, allCategories }) {
  const equipped = {};
  allCategories.forEach(cat => {
    const itemId = accessories[cat.id];
    if (itemId) {
      const item = cat.items.find(i => i.id === itemId);
      if (item) equipped[cat.id] = item;
    }
  });

  return (
    <svg viewBox="0 0 200 280" className="w-full h-full" style={{ maxHeight: 230 }}>
      {/* Subtle silhouette */}
      <circle cx="100" cy="42" r="26" fill="#D8C3B5" opacity="0.18" />
      <ellipse cx="100" cy="130" rx="38" ry="50" fill="#D8C3B5" opacity="0.12" />
      <ellipse cx="80" cy="225" rx="12" ry="40" fill="#D8C3B5" opacity="0.10" />
      <ellipse cx="120" cy="225" rx="12" ry="40" fill="#D8C3B5" opacity="0.10" />

      {equipped.hat && (
        <g>{equipped.hat.id === "eyepatch" ? (
          <><ellipse cx="88" cy="40" rx="11" ry="8" fill={equipped.hat.color} opacity="0.9" /><path d="M77 35 Q100 30 123 35" fill="none" stroke={equipped.hat.color} strokeWidth="2" opacity="0.7" /></>
        ) : (
          <><rect x="68" y="10" width="64" height="26" rx="6" fill={equipped.hat.color} opacity="0.9" /><rect x="58" y="30" width="84" height="7" rx="3" fill={equipped.hat.color} opacity="0.7" />{equipped.hat.accent && <rect x="72" y="18" width="16" height="3" rx="1" fill={equipped.hat.accent} opacity="0.5" />}</>
        )}</g>
      )}
      {equipped.bandana && (
        <g>
          <path d="M78 72 Q100 68 122 72 Q122 80 100 78 Q78 80 78 72" fill={equipped.bandana.color} opacity="0.85" />
          <circle cx="100" cy="78" r="4" fill={equipped.bandana.color} opacity="0.7" />
          <path d="M96 80 L88 94 L92 95" fill={equipped.bandana.color} opacity="0.55" />
          <path d="M104 80 L112 94 L108 95" fill={equipped.bandana.color} opacity="0.55" />
        </g>
      )}
      {equipped.shirt && (
        <g>
          <path d="M65 82 L135 82 L130 148 L70 148 Z" fill={equipped.shirt.color} opacity="0.85" />
          <path d="M84 82 L100 93 L116 82" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <path d="M65 85 L45 105 L52 109 L68 93" fill={equipped.shirt.color} opacity="0.7" />
          <path d="M135 85 L155 105 L148 109 L132 93" fill={equipped.shirt.color} opacity="0.7" />
          {equipped.shirt.pattern === "stripes" && [0,1,2,3].map(i => <rect key={i} x="70" y={90+i*13} width="60" height="3" fill="white" opacity="0.2" rx="1" />)}
        </g>
      )}
      {equipped.pants && (
        <g>
          <path d="M70 148 L66 260 L90 260 L100 175 L110 260 L134 260 L130 148 Z" fill={equipped.pants.color} opacity="0.85" />
          <rect x="68" y="146" width="64" height="6" rx="2" fill={equipped.pants.color} opacity="0.5" />
        </g>
      )}
      {equipped.weapon && (
        <g transform="translate(162,145) rotate(15)">
          {(equipped.weapon.id === "wooden_sword" || equipped.weapon.id === "metal_dagger") ? (
            <><rect x="-3" y="-30" width="6" height="48" rx="2" fill={equipped.weapon.color} opacity="0.85" /><rect x="-8" y="14" width="16" height="5" rx="2" fill={equipped.weapon.color} opacity="0.6" /></>
          ) : equipped.weapon.id === "flute" ? (
            <>
              {/* Silver flute - horizontal tube with keys */}
              <rect x="-20" y="-2" width="40" height="4" rx="2" fill="#C0C0C0" opacity="0.9" />
              <rect x="-22" y="-3" width="6" height="6" rx="3" fill="#D4D4D8" opacity="0.7" />
              {[0,1,2,3].map(i => <circle key={i} cx={-8+i*8} cy="0" r="1.5" fill="#A1A1AA" opacity="0.6" />)}
            </>
          ) : (
            <>
              {/* Lute shape */}
              <ellipse cx="0" cy="5" rx="10" ry="14" fill={equipped.weapon.color} opacity="0.8" />
              <rect x="-2" y="-32" width="4" height="28" rx="1" fill={equipped.weapon.color} opacity="0.6" />
              <rect x="-6" y="-34" width="12" height="5" rx="2" fill={equipped.weapon.color} opacity="0.5" />
              {/* Strings */}
              {[-2,0,2].map(x => <line key={x} x1={x} y1="-5" x2={x} y2="15" stroke="#FFC107" strokeWidth="0.5" opacity="0.4" />)}
            </>
          )}
        </g>
      )}
      {equipped.tool && (
        <g transform="translate(28,90)">
          {equipped.tool.id === "telescope" ? (
            <g transform="rotate(-30)">
              {/* Telescope tube */}
              <rect x="-3" y="-22" width="6" height="14" rx="2" fill="#D97706" opacity="0.9" />
              <rect x="-4.5" y="-8" width="9" height="20" rx="3" fill="#B45309" opacity="0.85" />
              <rect x="-6" y="10" width="12" height="6" rx="2" fill="#92400E" opacity="0.7" />
              {/* Lens */}
              <ellipse cx="0" cy="-22" rx="4" ry="2" fill="#87CEEB" opacity="0.5" />
            </g>
          ) : equipped.tool.id === "compass" ? (
            <>
              <circle cx="0" cy="0" r="12" fill="#F5DEB3" opacity="0.3" />
              <circle cx="0" cy="0" r="12" fill="none" stroke={equipped.tool.color} strokeWidth="2" opacity="0.8" />
              <path d="M0 -8 L3 0 L0 8 L-3 0 Z" fill="#DC2626" opacity="0.6" />
              <circle cx="0" cy="0" r="2" fill="#4A3224" opacity="0.5" />
            </>
          ) : equipped.tool.id === "satchel" ? (
            <>
              <rect x="-12" y="-8" width="24" height="18" rx="4" fill={equipped.tool.color} opacity="0.8" />
              <rect x="-8" y="-12" width="16" height="6" rx="2" fill={equipped.tool.color} opacity="0.5" />
              <circle cx="0" cy="2" r="2.5" fill="#FFC107" opacity="0.4" />
            </>
          ) : equipped.tool.id === "treasure_map" ? (
            <>
              <text x="0" y="5" textAnchor="middle" fontSize="25">{equipped.tool.emoji || "🗺️"}</text>
            </>
          ) : (
            <rect x="-10" y="-12" width="20" height="16" rx="2" fill="#F5DEB3" opacity="0.8" />
          )}
        </g>
      )}

      {!equipped.hat && !equipped.shirt && !equipped.pants && (
        <text x="100" y="140" textAnchor="middle" fontSize="11" fill="#B5A08C" opacity="0.5" fontFamily="Fredoka">Buy items to outfit!</text>
      )}
    </svg>
  );
}

function ClothingTile({ item, size = 48, owned, wearing }) {
  return (
    <div className="relative rounded-lg overflow-hidden" style={{ width: size, height: size, background: item.image ? "transparent" : item.color, border: `2px solid ${wearing ? "#FFC107" : owned ? "#4ADE80" : item.color+"60"}` }}>
      {item.image ? (
        <img src={item.image} alt={item.name} className="w-full h-full object-cover rounded-md" />
      ) : (
        <>
          {item.emoji && <div className="absolute inset-0 flex items-center justify-center" style={{ fontSize: size * 0.55 }}>{item.emoji}</div>}
          {item.pattern === "stripes" && [0,1,2].map(i => <div key={i} className="absolute" style={{ width:"100%", height:size/6, background:"rgba(255,255,255,0.3)", top:i*(size/3) }} />)}
          {item.pattern === "shimmer" && <div className="absolute inset-0" style={{ background:"linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)" }} />}
        </>
      )}
      {wearing && <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" /></div>}
      {owned && !wearing && <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-green-400 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" /></div>}
    </div>
  );
}

export default function StorePage({ stats: propStats, refreshStats }) {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const [stats, setStats] = useState(propStats);
  const [activeCategory, setActiveCategory] = useState(null);

  React.useEffect(() => { if (propStats) setStats(propStats); }, [propStats]);

  if (!stats) return <div className="text-center py-20 font-heading" style={{ color: "#7A5946" }}>Loading...</div>;

  const currentRank = stats.rank?.rank || 1;
  const accessories = stats.avatar_accessories || {};
  const purchased = stats.purchased_items || {};

  const isOwned = (catId, itemId) => {
    return (purchased[catId] || []).includes(itemId) || accessories[catId] === itemId;
  };

  const buyOrEquip = async (catId, item) => {
    try {
      const { data } = await axios.post(`${API}/users/${currentUser.id}/buy-accessory`, {
        category: catId, item_id: item.id, cost: item.cost
      });
      if (!isOwned(catId, item.id)) playCoinSound();
      setCurrentUser(data);
      setStats(prev => ({ ...prev, gold_coins: data.gold_coins, avatar_accessories: data.avatar_accessories, purchased_items: data.purchased_items || prev.purchased_items }));
      if (refreshStats) refreshStats();
      toast.success(isOwned(catId, item.id) ? `Switched to ${item.name}!` : `${item.name} purchased!`);
    } catch (e) { toast.error(e.response?.data?.detail || "Not enough coins"); }
  };

  // Category detail
  if (activeCategory) {
    const cat = STORE_CATEGORIES.find(c => c.id === activeCategory);
    const isUnlocked = currentRank >= cat.unlockRank;
    return (
      <div className="slide-up" data-testid="store-category-view">
        <button onClick={() => setActiveCategory(null)} className="flex items-center gap-2 mb-4 font-heading font-semibold" style={{ color: "#7A5946" }} data-testid="back-to-store">
          <ArrowLeft className="w-5 h-5" /> Back to Closet
        </button>
        <div className="open-book p-5">
          <h2 className="font-heading text-lg font-bold mb-3" style={{ color: "#4A3224" }}>{CAT_EMOJI[cat.id]} {cat.label}</h2>
          {!isUnlocked ? (
            <div className="parchment rounded-xl p-6 text-center">
              <Lock className="w-10 h-10 mx-auto mb-2" style={{ color: "#A0896C" }} />
              <p className="font-heading text-sm" style={{ color: "#7A5946" }}>Reach Rank {cat.unlockRank} ({RANKS[cat.unlockRank-1]?.name}) to unlock!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cat.items.map(item => {
                const isWearing = accessories[cat.id] === item.id;
                const owned = isOwned(cat.id, item.id);
                const canAfford = stats.gold_coins >= item.cost;
                return (
                  <div key={item.id} className={`tt-card p-3 flex items-center gap-3 ${isWearing ? "ring-2 ring-amber-400" : ""}`} data-testid={`store-item-${item.id}`}>
                    <ClothingTile item={item} size={52} owned={owned} wearing={isWearing} />
                    <div className="flex-1 min-w-0">
                      <p className="font-heading text-sm font-semibold" style={{ color: "#4A3224" }}>{item.name}</p>
                      <p className="text-[10px]" style={{ color: "#7A5946" }}>{item.desc}</p>
                    </div>
                    {isWearing ? (
                      <span className="text-xs font-heading font-bold px-3 py-1 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}>Wearing</span>
                    ) : owned ? (
                      <button onClick={() => buyOrEquip(cat.id, item)}
                        className="tt-btn tt-btn-ocean px-3 py-1.5 text-xs flex items-center gap-1"
                        data-testid={`swap-${item.id}`}>
                        <RefreshCw className="w-3 h-3" /> Swap
                      </button>
                    ) : (
                      <button onClick={() => buyOrEquip(cat.id, item)} disabled={!canAfford}
                        className="tt-btn tt-btn-gold px-3 py-1.5 text-xs disabled:opacity-40 flex items-center gap-1"
                        data-testid={`buy-${item.id}`}>
                        <img src={GOLD_COIN_URL} alt="" className="w-4 h-4 object-contain rounded-full" />
                        {item.cost}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  const wearingItems = CAT_ORDER.map(catId => {
    const itemId = accessories[catId];
    if (!itemId) return null;
    const cat = STORE_CATEGORIES.find(c => c.id === catId);
    const item = cat?.items.find(i => i.id === itemId);
    return item ? { ...item, catId, catLabel: cat.label } : null;
  }).filter(Boolean);

  return (
    <div className="slide-up" data-testid="store-page">
      {/* Display */}
      <div className="open-book p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>Your Closet</h2>
          <div className="flex items-center gap-1">
            <img src={GOLD_COIN_URL} alt="coin" className="w-5 h-5 object-contain rounded-full" />
            <span className="font-heading text-lg font-bold" style={{ color: "#D97706" }}>{stats.gold_coins}</span>
          </div>
        </div>
        <div className="flex gap-3" style={{ minHeight: 200 }}>
          <div className="flex-1 rounded-xl p-2" style={{ background: "linear-gradient(180deg, #FDFBF7, #F5E6D0)", border: "2px solid #D8C3B5" }} data-testid="outfit-display">
            <OutfitDisplay accessories={accessories} allCategories={STORE_CATEGORIES} />
          </div>
          <div className="w-[120px] flex-shrink-0 rounded-xl p-2" style={{ background: "#F5F0E8", border: "2px solid #D8C3B5" }} data-testid="wearing-sidebar">
            <p className="font-heading text-[9px] font-bold text-center mb-1.5" style={{ color: "#7A5946" }}>Equipped</p>
            {wearingItems.length === 0 ? (
              <p className="text-[9px] text-center mt-4" style={{ color: "#B5A08C" }}>No items yet</p>
            ) : (
              <div className="space-y-1.5">
                {wearingItems.map(item => (
                  <div key={item.catId} className="flex items-center gap-1.5 p-1 rounded-md" style={{ background: "white" }}>
                    <div className="w-6 h-6 rounded flex-shrink-0" style={{ background: item.color, border: `1px solid ${item.color}80` }} />
                    <div className="min-w-0">
                      <p className="text-[8px] font-semibold truncate" style={{ color: "#4A3224" }}>{item.name}</p>
                      <p className="text-[7px]" style={{ color: "#7A5946" }}>{item.catLabel}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Shopping */}
      <div className="open-book p-5">
        <h3 className="font-heading text-base font-bold mb-3" style={{ color: "#4A3224" }}>Browse & Purchase</h3>
        <div className="space-y-2">
          {STORE_CATEGORIES.map(cat => {
            const isUnlocked = currentRank >= cat.unlockRank;
            const equippedItem = cat.items.find(i => i.id === accessories[cat.id]);
            const ownedCount = (purchased[cat.id] || []).length + (accessories[cat.id] && !(purchased[cat.id] || []).includes(accessories[cat.id]) ? 1 : 0);
            return (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className="tt-card w-full p-3 flex items-center gap-3 text-left" data-testid={`store-cat-${cat.id}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: isUnlocked ? "#FEF3C7" : "#F5F0E8", border: `2px solid ${isUnlocked ? "#FFC107" : "#D8C3B5"}` }}>
                  {isUnlocked ? <span className="text-lg">{CAT_EMOJI[cat.id]}</span> : <Lock className="w-4 h-4" style={{ color: "#D8C3B5" }} />}
                </div>
                <div className="flex-1">
                  <p className="font-heading text-sm font-semibold" style={{ color: isUnlocked ? "#4A3224" : "#A0896C" }}>{cat.label}</p>
                  <p className="text-[10px]" style={{ color: isUnlocked ? "#7A5946" : "#A0896C" }}>
                    {isUnlocked ? (equippedItem ? `Wearing: ${equippedItem.name}` : `${cat.items.length} items`) : `Unlock at Rank ${cat.unlockRank}`}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "#D8C3B5" }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
